import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SearchForm from "../../../component/searchPage/SearchForm";
import SearchDropdown from "../../../component/searchPage/SearchDropdown";
import SearchInput from "../../../component/searchPage/SearchInput";
import {
  deleteContract, getContractCriteria, searchContracts,
  updateLockStatus, downloadContractPDF, downloadAllContractResult
} from "../../../service/contract";
import { PdfLink } from "../../../component/searchPage/PdfLink";
import { Delete, Edit } from "@mui/icons-material";
import { Button, Checkbox, Snackbar, Alert, CircularProgress } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SEARCH_RESULT_KEY } from '../../../component/searchPage/SearchResult';
import { useTranslation } from "react-i18next";
import SnackBar from '../../../component/snackbarContent/SnackBar';

const TITLE = 'Search Contracts';

const exportToolbar = (t) => ({
	type: 'contract',
	pdf: {
		download_selected: {
			isrequired: true,
			label: t('Download Selected Files (PDF)'),
			warning: t('Please select at least one Contract'),
			severity: 'warning'
		},
		download_all: {
			isrequired: true,
			label: t('Download All Files (PDF)')
		}
	},
	excel: {
		download_selected: {
			isrequired: true,
			label: t('Export Selected to Excel'),
			warning: t('Please select at least one Contract'),
			severity: 'warning'
		},
		download_all: {
			isrequired: true,
			label: t('Export All to Excel')
		}
	},
	email: {
		email_selected_pdf: {
			isrequired: true,
			label: t('Email Selected Files (PDF)'),
			warning: t('Please select at least one Contract'),
			severity: 'warning'
		},
		email_all_pdf: {
			isrequired: true,
			label: t('Email All Files (PDF)')
		}
	}
});


export function renderContractLink(params) {
  const { pdf_doc_name, link } = params.value ?? '';
  return (
    <PdfLink href={`${link}`} tabIndex={params.tabIndex}>
      {pdf_doc_name}
    </PdfLink>
  );
}

const columns = (handleAmendClick, handleDeleteClick, handleLockChange, t) => [
  { field: 'contract_number', headerName: t('Contract Number'), width: 170, sort: 'asc' },
  { field: 'contract_or_schedule', headerName: t('Type'), width: 150 },
  { field: 'soldto_name', headerName: t('SoldTo Name'), width: 200 },
  { field: 'soldto_number', headerName: t('SoldTo Account'), width: 150 },
  { field: 'pdf_doc_name', headerName: t('Contract Document'), width: 400, renderCell: renderContractLink, valueFormatter: (value) => value.pdf_doc_name },
  { field: 'sales_office', headerName: t('Sales Office'), width: 115 },
  { field: 'contract_year', headerName: t('Year'), width: 100 },
  { field: 'contract_user_type', headerName: t(t('User Type')), width: 120 },
  { field: 'acceptance_status', headerName: t('Accept Status'), width: 150 },
  {
    field: 'lock_status',
    headerName: t('Lock Status'),
    width: 150,
    renderCell: (params) => (
      <Checkbox
        checked={params.value}
        onChange={() => handleLockChange(params.row)}
        color="primary"
      />
    ),
  },
  {
    field: 'schedule_amendments',
    headerName: t('Schedule Amendments'),
    width: 200,
    renderCell: (params) => (
      params.row.acceptance_status === 'Accepted' && params.row.contract_or_schedule === 'Contract' ? (
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<Edit />}
          onClick={() => handleAmendClick(params.row.contract_number)}
        >
          {t("Amend Schedule")}
        </Button>
      ) : (
        <span>{t("Not Available")}</span>
      )
    ),
  },
  {
    field: 'action',
    headerName: t('Action'),
    width: 150,
    renderCell: (params) => {
      const isPending = params.row.acceptance_status === 'Pending User Review';
      return (
        <div className="actions">
          <button
            className="delete-btn"
            onClick={() => handleDeleteClick(params.row)}
            disabled={!isPending}
            style={{
              cursor: isPending ? 'pointer' : 'not-allowed',
              color: 'inherit',
              border: 'none',
              background: 'none',
              padding: 0,
            }}
          >
            <Delete
              fontSize="small"
              style={{ color: isPending ? 'red' : 'gray' }}
            />
          </button>
        </div>
      );
    },
  },
];

function SearchContracts() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient=useQueryClient()
  const [snackbar, setSnackbar] = useState({ open: false, message: '', type: '', errorType: "tryAndCatch" });
  const handleCloseSnackbar = () => setSnackbar({ open: false, message: '', type: '' });
  const [loading, setLoading] = useState(false);
  const deleteMutation=useMutation({
    mutationFn:async(contract)=>deleteContract(contract),
    onSuccess:()=>{
      console.log("Invalidation ...")
      queryClient.invalidateQueries({queryKey:[TITLE,SEARCH_RESULT_KEY]})
    },
    onError:()=>{
      setSnackbar({ open: true, message: 'Something went wrong. Please try again later.', type: 'error' });
    }
  });

  const lockMutation = useMutation({
    mutationFn: async (contract) => updateLockStatus(contract),
    onMutate: () => {
      setLoading(true);
    },
    onSuccess: () => {
      console.log("Lock status updated ...");
      queryClient.invalidateQueries({ queryKey: [TITLE, SEARCH_RESULT_KEY] });
      setSnackbarMessage('Lock status updated successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setLoading(false);
    },
    onError: () => {
      console.error('Failed to update lock status');
      setSnackbarMessage('Failed to update lock status');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setLoading(false);
    }
  });

  const handleAmendClick = (contractNumber) => {
    console.log("Amending Contract:::::", contractNumber);
    navigate(`/schedule-amendments/${contractNumber}`);
  };

  const handleDeleteClick = async (contract) => {
    console.log("Deleting the contract:::::", contract);
    try {
      deleteMutation.mutate(contract);
    } catch (error) {
      console.error('Failed to delete contract:', error);
    }
  };

  const handleLockChange = (contract) => {
    const updatedContract = { ...contract, lock_status: !contract.lock_status };
    lockMutation.mutate(updatedContract);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <>
      <SearchForm
        title={t(TITLE)}
        requiredInputs={1}
        columns={columns(handleAmendClick,handleDeleteClick,handleLockChange, t)}
        criteriaFn={getContractCriteria}
        searchFn={searchContracts}
			  exportToolbar={exportToolbar(t)}
        getAllResult={downloadAllContractResult}
        downloadFns={{title:'Contracts', zip: downloadContractPDF, excel: () => {} }}
      >
        <div className="search__body--column">
          <SearchInput label={t("Contract #")} dataLabel="contractNumber" />
          <SearchInput label={t("Sold To Account")} dataLabel="soldToAccount" />
          <SearchInput label={t("Sales Office")} dataLabel="salesOffice" />
          <SearchDropdown label={t("Select Year")} dataLabel="yearList" />
        </div>
        <div className="search__body--column">
          <SearchDropdown label={t("Contract Template")} dataLabel="templateList" />
          <SearchDropdown label={t("Contract Status")} dataLabel="statusType" />
          <SearchDropdown label={t("Contract User Type")} dataLabel="userTypes" />
        </div>
      </SearchForm>
      <SnackBar handleCloseSnackbar={handleCloseSnackbar} snackbar={snackbar} />
    </>
  );
}

export default SearchContracts;